/**
 * Линза консоли подтверждения: дневная vs недельная (задача 116 §2.2).
 *
 * Режим подтверждения — свойство юнита (`approval_mode`, §1.1). Актор может вести и
 * daily-, и weekly-юниты; линзу выбираем из `GET /api/units` ∩ полномочие актора
 * (admin — все юниты по роли; лид/зам — `acting_unit_ids`):
 *  - есть и daily-, и weekly-юниты → переключатель «День | Неделя» (день = прежняя
 *    консоль без изменений; составы не смешиваются);
 *  - только weekly → сразу лента (дневная консоль заменена UI-линзой);
 *  - только daily (или юниты ещё не подъехали) → прежняя дневная консоль.
 *
 * `TimeApprovalPage` перегружена — вся эта развилка живёт здесь, страница лишь
 * рендерит линзу. Дневную консоль (`ApprovalConsole`) не трогаем, недельную ленту
 * (`WeekFeedConsole`) строим отдельно.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";

import { SegmentedStatus } from "@/components/ui/segmented-status";
import { ApprovalConsole } from "@/components/approval/ApprovalConsole";
import { ApprovalUnitPicker, ApprovalUnitSelectionSync } from "@/components/approval/UnitPicker";
import {
  approvalUnitsForMode,
  approvalUnitsInScope,
  resolveApprovalUnitSelection,
} from "@/components/approval/approvalUnitSelection";
import { useUnits } from "@/hooks/useUnits";
import { useSession } from "@/store/session";
import { WeekFeedConsole } from "./WeekFeedConsole";

type Lens = "day" | "week";

interface ApprovalLensProps {
  weekStart: string;
  selectedDate: string;
  selectedUnitId: number | null;
  includeChildren?: boolean;
  openUserId?: number | null;
  onWeekStartChange: (weekStart: string) => void;
  onSelectedDateChange: (date: string) => void;
  onUnitChange: (unitId: number | null) => void;
  onIncludeChildrenChange?: (checked: boolean) => void;
}

export function ApprovalLens(props: ApprovalLensProps): React.ReactElement {
  const { t } = useTranslation();
  const role = useSession((s) => s.user?.role);
  const actingUnitIds = useSession((s) => s.user?.acting_unit_ids);
  const unitsQuery = useUnits({ refetchOnMount: "always" });

  const scopedUnits = React.useMemo(
    () => approvalUnitsInScope(unitsQuery.data ?? [], role, actingUnitIds),
    [unitsQuery.data, role, actingUnitIds],
  );
  const dailyUnits = React.useMemo(() => approvalUnitsForMode(scopedUnits, "daily"), [scopedUnits]);
  const weeklyUnits = React.useMemo(
    () => approvalUnitsForMode(scopedUnits, "weekly"),
    [scopedUnits],
  );
  const hasWeekly = weeklyUnits.length > 0;
  const hasDaily = dailyUnits.length > 0;

  // Дефолт следует за scope (однородный weekly → лента, иначе день); явный выбор
  // пользователя (только при смешанном scope) перебивает дефолт. Валидный
  // deep-link `unit_id` сразу открывает линзу режима этого подразделения.
  const [lensOverride, setLensOverride] = React.useState<Lens | null>(null);
  const selectedUnitMode = scopedUnits.find(
    (unit) => unit.id === props.selectedUnitId,
  )?.approval_mode;
  const preferredLens: Lens =
    lensOverride ??
    (selectedUnitMode === "weekly"
      ? "week"
      : selectedUnitMode === "daily"
        ? "day"
        : hasWeekly && !hasDaily
          ? "week"
          : "day");
  const lens: Lens =
    preferredLens === "week" && hasWeekly ? "week" : hasDaily ? "day" : hasWeekly ? "week" : "day";
  const showToggle = unitsQuery.isFetchedAfterMount && hasWeekly && hasDaily;
  const lensUnits = lens === "week" ? weeklyUnits : dailyUnits;
  const selection = resolveApprovalUnitSelection({
    units: lensUnits,
    role,
    selectedUnitId: props.selectedUnitId,
    ready: role !== undefined && unitsQuery.isFetchedAfterMount,
  });
  const sceneKey = `${selection.effectiveUnitId ?? "none"}:${props.includeChildren ? "subtree" : "exact"}`;

  return (
    <div className="flex flex-col gap-4">
      <ApprovalUnitSelectionSync
        selectedUnitId={props.selectedUnitId}
        nextUnitId={selection.nextUrlUnitId}
        onUnitChange={props.onUnitChange}
      />
      {showToggle && (
        // Обёртка `flex` — контрол `inline-flex`, в `flex-col`-родителе рамка
        // растягивалась на всю ширину при сегментах на ~177px (грабля
        // `patterns.md §StatusSegmentedControl`); здесь `stretch` не нужен —
        // 2 сегмента режима на весь экран растягивать не хотим.
        <div className="flex">
          <SegmentedStatus<Lens>
            ariaLabel={t("week.lens.label")}
            data-testid="approval-lens"
            value={lens}
            onChange={setLensOverride}
            options={[
              { value: "day", label: t("week.lens.day"), tone: "tone-neutral" },
              { value: "week", label: t("week.lens.week"), tone: "tone-neutral" },
            ]}
          />
        </div>
      )}

      <ApprovalUnitPicker
        visible={selection.showPicker}
        units={lensUnits}
        selectedUnitId={selection.effectiveUnitId}
        onChange={props.onUnitChange}
        includeChildren={props.includeChildren}
        onIncludeChildrenChange={props.onIncludeChildrenChange}
        showIncludeChildren
      />

      {lens === "week" && hasWeekly ? (
        <WeekFeedConsole
          key={sceneKey}
          weekStart={props.weekStart}
          unitId={selection.effectiveUnitId}
          includeChildren={props.includeChildren ?? false}
          enabled={selection.queriesEnabled}
          onWeekStartChange={props.onWeekStartChange}
        />
      ) : (
        <ApprovalConsole
          key={sceneKey}
          weekStart={props.weekStart}
          selectedDate={props.selectedDate}
          selectedUnitId={selection.effectiveUnitId}
          includeChildren={props.includeChildren ?? false}
          queriesEnabled={selection.queriesEnabled}
          needsUnit={selection.needsUnit}
          openUserId={props.openUserId}
          onWeekStartChange={props.onWeekStartChange}
          onSelectedDateChange={props.onSelectedDateChange}
        />
      )}
    </div>
  );
}
